// Globals
import React, { ChangeEvent, ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash.debounce';

// Store
import { useAppDispatch, useAppState } from '@/store';
import { changeProductOfMonthQuery, getProductsOfMonthList, productsSelector } from '../../feature';

// Components
import { StyledFilterRow, StyledSelect } from './ProductOfMonthFiltersContainerStyles';
import { RangePicker } from '@/components';
import { Input, Select } from '@/ui';

// Modules
import { getCategoriesOptions, useCategoriesAutocomplete } from '@/modules/Categories';
import { Resource, usePermissions } from '@/modules/Permissions';

// Helpers
import { getActiveOptions, getStoresOptions, ProductsOfMonthFiltersFields } from '../../helpers';
import { GetProductsOfMonthListDTO } from '../../dtos';
import { useStoresSearch } from '../../hooks';

type TProductOfMonthFiltersContainer = {
  children?: ReactNode;
  className?: string;
};

const ProductOfMonthFiltersContainer = ({ children, className }: TProductOfMonthFiltersContainer) => {
  const { t } = useTranslation(['products']);
  const dispatch = useAppDispatch();
  const { productsOfMonthQuery } = useAppState(productsSelector);
  const { storeList, searchStores } = useStoresSearch();
  const { list: categoriesList, onSearch: onCategoriesSearch } = useCategoriesAutocomplete({});
  const { canManage } = usePermissions();
  // The trailing actions column only exists when the admin can manage products of the month.
  const canManageProductOfMonth = canManage(Resource.PRODUCT_OF_MONTH);

  const handleDebounceSearch = useMemo(
    () =>
      debounce(async (searchParams) => {
        const dto = new GetProductsOfMonthListDTO(searchParams);
        dispatch(getProductsOfMonthList(dto));
      }, 500),
    [dispatch],
  );

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const newQuery = { ...productsOfMonthQuery, [name]: value || undefined };
    dispatch(changeProductOfMonthQuery(newQuery));
    handleDebounceSearch(newQuery);
  };

  const handleBooleanFilter = (name: string) => (value: string) => {
    const newQuery = { ...productsOfMonthQuery, [name]: value };
    dispatch(changeProductOfMonthQuery(newQuery));
    handleDebounceSearch(newQuery);
  };

  const handleDateRangeChange = (_: unknown, dateStrings: [string, string]) => {
    const newQuery = {
      ...productsOfMonthQuery,
      [ProductsOfMonthFiltersFields.DATE_FROM]: dateStrings[0] || undefined,
      [ProductsOfMonthFiltersFields.DATE_TO]: dateStrings[1] || undefined,
      page: 1,
    };
    dispatch(changeProductOfMonthQuery(newQuery));
    handleDebounceSearch(newQuery);
  };

  const onSelectChange = (value: unknown, key: ProductsOfMonthFiltersFields) => {
    const newQuery = { ...productsOfMonthQuery, [key]: value || undefined };
    dispatch(changeProductOfMonthQuery(newQuery));
    handleDebounceSearch(newQuery);
  };

  return (
    <thead className={className}>
      {children}
      <StyledFilterRow>
        <th>
          <StyledSelect
            allowClear
            showSearch
            isMultiselect
            options={getStoresOptions(storeList ?? [])}
            onSearch={searchStores}
            value={productsOfMonthQuery?.store_ids}
            onChange={(v) => onSelectChange(v, ProductsOfMonthFiltersFields.STORE_ID)}
          />
        </th>
        <th>
          <StyledSelect
            allowClear
            showSearch
            options={getCategoriesOptions(categoriesList ?? [])}
            onSearch={onCategoriesSearch}
            value={productsOfMonthQuery?.category_id}
            onChange={(v) => onSelectChange(v, ProductsOfMonthFiltersFields.CATEGORY_ID)}
          />
        </th>
        <th>
          <Input
            placeholder={t('products_list_sku_search')}
            name={ProductsOfMonthFiltersFields.PRODUCT_SKU}
            onChange={handleSearch}
            value={productsOfMonthQuery?.product_sku}
            size="large"
            allowClear
          />
        </th>
        <th>
          <RangePicker
            placeholder={[
              t('orders_range_picker_placeholder_from', { ns: 'orders' }),
              t('orders_range_picker_placeholder_to', { ns: 'orders' }),
            ]}
            onChange={handleDateRangeChange}
            size="middle"
          />
        </th>
        <th>
          <Select
            options={getActiveOptions(t)}
            placeholder={t('products_list_status')}
            onChange={handleBooleanFilter(ProductsOfMonthFiltersFields.PUBLISHED)}
            value={productsOfMonthQuery?.published}
            size="large"
            allowClear
          />
        </th>
        {canManageProductOfMonth && <th />}
      </StyledFilterRow>
    </thead>
  );
};

export default ProductOfMonthFiltersContainer;
